/** 单图层属性面板：通用区块（位置/锚点/样式）+ 物料 props（自动生成）+ 动画 + 事件。 */
import { Collapse, Input, InputNumber, Radio, Slider, Space, Switch, Tag, Tooltip } from 'antd';
import { LockOutlined, UnlockOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import type { Layer } from '@h5/schema';
import { getMaterial } from '@h5/materials';
import { useEditorStore } from '../store/store';
import { groupFields, schemaToFields } from './schema-to-fields';
import { SETTERS } from './setters';
import { Field } from './Field';
import { AnimationsEditor } from './AnimationsEditor';
import { EventsEditor } from './EventsEditor';
import { useMemo } from 'react';

export function LayerPanel({ layer }: { layer: Layer }) {
  const doc = useEditorStore((s) => s.doc);
  const currentPageId = useEditorStore((s) => s.ui.currentPageId);
  const { updateLayer, updateLayerProps, moveLayer } = useEditorStore.getState();
  const def = getMaterial(layer.type);

  const groups = useMemo(() => groupFields(schemaToFields(def?.propsSchema)), [def]);

  const rectField = (key: 'x' | 'y' | 'w' | 'h' | 'rotate', label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 12, color: '#888', width: 14 }}>{label}</span>
      <InputNumber size="small" style={{ width: 70 }} value={layer.rect[key]} onChange={(v) => moveLayer(layer.id, { [key]: v ?? 0 })} />
    </div>
  );

  const items = [
    {
      key: 'base',
      label: '基础',
      children: (
        <>
          <Field label="名称"><Input size="small" value={layer.name} placeholder={def?.name ?? layer.type} onChange={(e) => updateLayer(layer.id, { name: e.target.value })} /></Field>
          <Field label="位置与尺寸">
            <Space wrap size={[8, 6]}>
              {rectField('x', 'X')}{rectField('y', 'Y')}{rectField('w', 'W')}{rectField('h', 'H')}{rectField('rotate', '°')}
            </Space>
          </Field>
          <Field label={<Tooltip title="屏幕比设计稿高时，图层跟随顶部 / 居中 / 底部">锚点</Tooltip>} inline>
            <Radio.Group size="small" optionType="button" buttonStyle="solid" value={layer.anchor}
              options={[{ label: '顶', value: 'top' }, { label: '中', value: 'center' }, { label: '底', value: 'bottom' }]}
              onChange={(e) => updateLayer(layer.id, { anchor: e.target.value })} />
          </Field>
          <Field label="透明度">
            <Slider min={0} max={1} step={0.01} value={layer.style.opacity} onChange={(v) => updateLayer(layer.id, { style: { ...layer.style, opacity: v } })} />
          </Field>
          <Field label="层级" inline>
            <InputNumber size="small" style={{ width: 70 }} value={layer.style.zIndex} onChange={(v) => updateLayer(layer.id, { style: { ...layer.style, zIndex: v ?? 0 } })} />
          </Field>
          <Space size={16}>
            <span style={{ fontSize: 12 }}>{layer.locked ? <LockOutlined /> : <UnlockOutlined />} 锁定 <Switch size="small" checked={layer.locked} onChange={(v) => updateLayer(layer.id, { locked: v })} /></span>
            <span style={{ fontSize: 12 }}>{layer.hidden ? <EyeInvisibleOutlined /> : <EyeOutlined />} 隐藏 <Switch size="small" checked={layer.hidden} onChange={(v) => updateLayer(layer.id, { hidden: v })} /></span>
          </Space>
        </>
      ),
    },
    ...groups.map((g) => ({
      key: `g-${g.group}`,
      label: g.group,
      children: (
        <>
          {g.fields.map((f) => {
            if (f.meta.visible && !f.meta.visible(layer.props)) return null;
            const Setter = SETTERS[f.meta.setter] ?? SETTERS.input;
            const inline = ['switch', 'color', 'radio'].includes(f.meta.setter);
            return (
              <Field key={f.key} label={f.meta.label} inline={inline}>
                <Setter value={layer.props[f.key]} onChange={(v) => updateLayerProps(layer.id, { [f.key]: v })} field={f} allProps={layer.props} />
              </Field>
            );
          })}
        </>
      ),
    })),
    {
      key: 'anim',
      label: <span>动画 {layer.animations.length > 0 && <Tag color="blue" style={{ marginLeft: 4 }}>{layer.animations.length}</Tag>}</span>,
      children: <AnimationsEditor value={layer.animations} onChange={(v) => updateLayer(layer.id, { animations: v })} />,
    },
    {
      key: 'events',
      label: <span>事件 {layer.events.length > 0 && <Tag color="green" style={{ marginLeft: 4 }}>{layer.events.length}</Tag>}</span>,
      children: <EventsEditor value={layer.events} doc={doc} currentPageId={currentPageId} onChange={(v) => updateLayer(layer.id, { events: v })} />,
    },
  ];

  return (
    <div>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>
        {def?.name ?? layer.type}
        {!def && <Tag color="red" style={{ marginLeft: 8 }}>未注册物料</Tag>}
      </div>
      <Collapse size="small" bordered={false} defaultActiveKey={['base', ...groups.map((g) => `g-${g.group}`), 'anim', 'events']} items={items} />
    </div>
  );
}
